/**
 * OpenAI Cost Display Component
 *
 * Shows the cost of OpenAI API usage for a product creation or optimization.
 */

import { useState } from 'react'
import { OpenAIUsage } from '../lib/api'

interface OpenAICostDisplayProps {
  usage: OpenAIUsage
  className?: string
  showDetails?: boolean
}

export function OpenAICostDisplay({ usage, className = '', showDetails = false }: OpenAICostDisplayProps) {
  const [expanded, setExpanded] = useState(false)

  const formatCurrency = (value: number, currency: string) => {
    if (value < 0.01) {
      return `<0.01 ${currency}`
    }
    return `${value.toFixed(4)} ${currency}`
  }

  const formatNumber = (value: number) => {
    return value.toLocaleString('es-ES')
  }

  return (
    <div className={`bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border border-emerald-200 dark:border-emerald-700 rounded-lg p-3 ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-lg">💰</span>
          <span className="text-sm font-medium text-emerald-800 dark:text-emerald-200">
            Coste OpenAI
          </span>
        </div>

        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-lg font-bold text-emerald-700 dark:text-emerald-300">
              {formatCurrency(usage.total_cost_eur, '€')}
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              ({formatCurrency(usage.total_cost_usd, '$')})
            </div>
          </div>

          {showDetails && usage.calls && usage.calls.length > 0 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300"
              title={expanded ? 'Ocultar detalles' : 'Ver detalles'}
            >
              <svg
                className={`w-5 h-5 transition-transform ${expanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Summary stats */}
      <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
        <div className="text-center p-1.5 bg-white/50 dark:bg-slate-800/50 rounded">
          <div className="font-semibold text-slate-700 dark:text-slate-300">{usage.num_calls}</div>
          <div className="text-slate-500 dark:text-slate-400">Llamadas</div>
        </div>
        <div className="text-center p-1.5 bg-white/50 dark:bg-slate-800/50 rounded">
          <div className="font-semibold text-slate-700 dark:text-slate-300">{formatNumber(usage.total_prompt_tokens)}</div>
          <div className="text-slate-500 dark:text-slate-400">Input</div>
        </div>
        <div className="text-center p-1.5 bg-white/50 dark:bg-slate-800/50 rounded">
          <div className="font-semibold text-slate-700 dark:text-slate-300">{formatNumber(usage.total_completion_tokens)}</div>
          <div className="text-slate-500 dark:text-slate-400">Output</div>
        </div>
      </div>

      {/* Expanded details */}
      {showDetails && expanded && usage.calls && usage.calls.length > 0 && (
        <div className="mt-3 border-t border-emerald-200 dark:border-emerald-700 pt-2">
          <div className="text-xs font-medium text-emerald-700 dark:text-emerald-300 mb-1">
            Detalle por llamada:
          </div>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {usage.calls.map((call, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between text-xs bg-white/30 dark:bg-slate-800/30 rounded p-1.5"
              >
                <div className="flex items-center gap-2">
                  <span className="font-mono text-slate-600 dark:text-slate-400">{idx + 1}.</span>
                  <span className="text-slate-700 dark:text-slate-300">{call.model}</span>
                  <span className="text-slate-500">
                    ({formatNumber(call.total_tokens)} tokens)
                  </span>
                </div>
                <span className="font-medium text-emerald-600 dark:text-emerald-400">
                  {formatCurrency(call.cost_eur, '€')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
